import { Heading, HeadingLevel, VisuallyHidden } from "@ariakit/react";
import { useMediaQuery } from "@indietabletop/appkit/use-media-query";
import { ReactElement } from "react";
import { Link } from "wouter";
import { atomic } from "../atomic.css";
import { LinkButton } from "../dialog-buttons";
import { Footer } from "../footer";
import { Icon } from "../icon";
import { links } from "../links";
import { MinWidth } from "../media";
import { NavItem, navItems } from "../nav-items";
import { VerticalDivider } from "../vertical-divider";
import * as css from "./style.css";

function ArmyBuilderIcon() {
  return (
    <svg
      width="64px"
      height="64px"
      viewBox="0 0 64 64"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      className={atomic({ flexShrink: 0 })}
    >
      <title>army_builder</title>
      <g id="army_builder" stroke="none" strokeWidth="1" fill="none">
        <path
          d="M32,48 C40.836556,48 48,40.836556 48,32 C48,23.163444 40.836556,16 32,16 C23.163444,16 16,23.163444 16,32 C16,40.836556 23.163444,48 32,48 Z"
          id="Oval"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeDasharray="0.5,4"
        ></path>
        <path
          d="M24.1964854,28.6060996 C29.3988285,31.6966212 32,33.2418821 32,33.2418821 C32,33.2418821 34.6011715,31.6966212 39.8035146,28.6060996"
          id="Path-4"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="square"
          transform="translate(32, 30.924) rotate(-180) translate(-32, -30.924)"
        ></path>
        <path
          d="M24.1964854,33.2418821 C29.3988285,36.3324038 32,37.8776646 32,37.8776646 C32,37.8776646 34.6011715,36.3324038 39.8035146,33.2418821"
          id="Path-4"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="square"
          transform="translate(32, 35.5598) rotate(-180) translate(-32, -35.5598)"
        ></path>
        <path
          d="M24.1964854,23.9703171 C29.3988285,27.0608387 32,28.6060996 32,28.6060996 C32,28.6060996 34.6011715,27.0608387 39.8035146,23.9703171"
          id="Path-4"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="square"
          transform="translate(32, 26.2882) rotate(-180) translate(-32, -26.2882)"
        ></path>
      </g>
    </svg>
  );
}

function ChangelogIcon() {
  return (
    <svg
      width="64px"
      height="64px"
      viewBox="0 0 64 64"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      className={atomic({ flexShrink: 0 })}
    >
      <g id="changelog" stroke="none" strokeWidth="1" fill="none">
        <polyline
          id="Path-3"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="square"
          points="11.0154694 31.7330102 37 32.0002 29.7294922 27.0002 29.7294922 36.4658203"
        ></polyline>
        <path
          d="M32,48 C40.836556,48 48,40.836556 48,32 C48,23.163444 40.836556,16 32,16 C23.163444,16 16,23.163444 16,32 C16,40.836556 23.163444,48 32,48 Z"
          id="Oval"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeDasharray="0.5,4"
        ></path>
        <circle
          id="Oval"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="square"
          cx="47.5"
          cy="32"
          r="5.5"
        ></circle>
      </g>
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg
      width="64px"
      height="64px"
      viewBox="0 0 64 64"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      className={atomic({ flexShrink: 0 })}
    >
      <g id="help" stroke="none" strokeWidth="1" fill="none">
        <path
          d="M32,48 C40.836556,48 48,40.836556 48,32 C48,23.163444 40.836556,16 32,16 C23.163444,16 16,23.163444 16,32 C16,40.836556 23.163444,48 32,48 Z"
          id="Oval"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeDasharray="0.5,4"
        ></path>
        <g
          id="Group"
          transform="translate(17, 20.636)"
          stroke="currentColor"
          strokeLinecap="square"
          strokeWidth="1.25"
        >
          <g id="Group-8" transform="translate(8, 4.364)">
            <circle id="Oval" cx="7" cy="7" r="7"></circle>
          </g>
          <line x1="15" y1="11.363961" x2="0.5" y2="25.863961" id="Line"></line>
          <polygon
            id="Triangle"
            transform="translate(20.5, 6.364) rotate(-135) translate(-20.5, -6.364)"
            points="20.5 2.36396103 25.5 10.363961 15.5 10.363961"
          ></polygon>
        </g>
      </g>
    </svg>
  );
}

function ListItem(props: {
  icon: NavItem["icon"];
  heading: string;
  description: string;
  href: string;
}) {
  const icons: Record<typeof props.icon, ReactElement> = {
    army: <ArmyBuilderIcon />,
    changelog: <ChangelogIcon />,
    help: <HelpIcon />,
  };

  return (
    <li
      className={atomic({
        borderBlockStart: {
          default: "dashed",
          first: "solid",
        },
        borderBlockEnd: {
          last: "dashed",
        },
      })}
    >
      <Link
        href={props.href}
        className={atomic({
          display: "flex",
          gap: 16,
          paddingBlock: 16,
          alignItems: "center",
          transition: "background-color",
          backgroundColor: {
            hover: "background-secondary",
          },
        })}
      >
        {icons[props.icon]}

        <div style={{ flexGrow: 1 }}>
          <Heading
            className={atomic({
              fontSize: "24-32",
              fontWeight: "black",
              fontFamily: "arboria",
              fontStyle: "italic",
              textTransform: "uppercase",
            })}
          >
            {props.heading}
          </Heading>

          <p
            className={atomic({
              marginBlockStart: 2,
              marginBlockEnd: 6,
              fontSize: 14,
              lineHeight: 1.5,
            })}
          >
            {props.description}
          </p>
        </div>

        <Icon name="forward" />
      </Link>
    </li>
  );
}

export function DashboardPage() {
  const isMedium = useMediaQuery(MinWidth.MEDIUM);
  const isWide = useMediaQuery(MinWidth.WIDE);

  if (!isWide) {
    return (
      <div>
        {!isMedium && (
          <div
            className={atomic({
              paddingInline: 24,
              paddingBlockStart: 24,
              paddingBlockEnd: 12,
            })}
          >
            <Heading
              className={atomic({
                fontFamily: "arboria",
                fontWeight: "black",
                fontSize: 32,
                textTransform: "uppercase",
                fontStyle: "italic",
              })}
            >
              Eternøl
            </Heading>
            <p
              className={atomic({
                marginBlockStart: 6,
                lineHeight: 1.5,
                fontSize: 14,
              })}
            >
              by Kye Browning & Dellon Myette, with Till Förster
            </p>
          </div>
        )}

        <TopLevelLinks />
        <PurchaseBox />
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={atomic({
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 6 minmax(0, 1fr)",
      })}
    >
      <div className={css.mainView}>
        <TopLevelLinks />

        <Footer />
      </div>

      <div className={atomic({ overflow: "hidden" })}>
        <VerticalDivider />
      </div>

      <PurchaseBox />
    </div>
  );
}

function TopLevelLinks() {
  return (
    <HeadingLevel>
      <div>
        <VisuallyHidden>
          <Heading>Eternøl</Heading>
        </VisuallyHidden>

        <HeadingLevel>
          <div
            className={atomic({
              padding: {
                default: 24,
                medium: 56,
              },
            })}
          >
            <ul>
              {navItems.map((item) => {
                return <ListItem {...item} key={item.href} />;
              })}
            </ul>
          </div>
        </HeadingLevel>
      </div>
    </HeadingLevel>
  );
}

function PurchaseBox() {
  return (
    <div className={css.purchaseBox}>
      <div className={css.purchaseContent}>
        <div className={css.bookContainer}>
          <div className={css.orbit}>
            <div className={css.planet}>
              <div className={css.moon} />
            </div>
          </div>

          <img src="/cover.webp" className={css.bookCover} />
        </div>

        <div className={css.ctaSection}>
          <LinkButton className={css.purchaseButton} href={links.eternol}>
            Get the rules
          </LinkButton>

          <p
            className={atomic({
              fontSize: 14,
              fontStyle: "italic",
              textAlign: "center",
              marginBlockStart: 16,
            })}
          >
            Via Electi Studio website
          </p>
        </div>
      </div>
    </div>
  );
}
